/**
 * API Error Handling Utilities
 *
 * Provides standardized error handling for API calls, including:
 * - Error classification (network, auth, validation, server)
 * - User-friendly error messages
 * - Retry logic for transient failures
 * - Error reporting integration
 * - Trace ID correlation for end-to-end observability
 */

import * as Sentry from "@sentry/react";

import { logger } from "@/lib/logger";
import { traceContext } from "@/lib/traceContext";

// =====================================================
// Supabase Error Code Constants
// =====================================================

/**
 * Centralized Supabase/PostgREST error codes.
 * Use the isSupabase* helpers below instead of checking codes directly.
 */
export const SUPABASE_ERROR_CODES = {
  /** Single-row query returned no rows (PostgREST) */
  NOT_FOUND: "PGRST116",
  /** Unique constraint violation (PostgreSQL) */
  UNIQUE_VIOLATION: "23505",
  /** Check constraint violation (PostgreSQL) */
  CHECK_VIOLATION: "23514",
  /** Unauthorized (PostgREST) */
  UNAUTHORIZED: "PGRST301",
  /** Forbidden (PostgREST) */
  FORBIDDEN: "PGRST302",
} as const;

/**
 * Type guard: error has a string `code` property (Supabase error shape).
 */
function hasErrorCode(error: unknown): error is { code: string } {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as Record<string, unknown>).code === "string"
  );
}

/**
 * Check if a Supabase error is "not found" (PGRST116).
 * Use this instead of `error.code === "PGRST116"`.
 */
export function isSupabaseNotFound(error: unknown): boolean {
  return hasErrorCode(error) && error.code === SUPABASE_ERROR_CODES.NOT_FOUND;
}

/**
 * Check if a Supabase error is a unique constraint violation (23505).
 */
export function isSupabaseUniqueViolation(error: unknown): boolean {
  return (
    hasErrorCode(error) && error.code === SUPABASE_ERROR_CODES.UNIQUE_VIOLATION
  );
}

/**
 * Check if a Supabase error is a check constraint violation (23514).
 */
export function isSupabaseCheckViolation(error: unknown): boolean {
  return (
    hasErrorCode(error) && error.code === SUPABASE_ERROR_CODES.CHECK_VIOLATION
  );
}

// Error types for classification
export type ApiErrorType =
  | "network"
  | "auth"
  | "forbidden"
  | "not_found"
  | "validation"
  | "rate_limit"
  | "server"
  | "unknown";

export interface ApiError {
  type: ApiErrorType;
  message: string;
  userMessage: string;
  statusCode?: number;
  originalError?: unknown;
  retryable: boolean;
  details?: Record<string, unknown>;
  /** Trace ID for correlation with backend logs */
  traceId: string;
}

// User-friendly messages for each error type
const ERROR_MESSAGES: Record<ApiErrorType, string> = {
  network:
    "Unable to connect to the server. Please check your internet connection.",
  auth: "Your session has expired. Please sign in again.",
  forbidden: "You don't have permission to perform this action.",
  not_found: "The requested resource was not found.",
  validation: "Please check your input and try again.",
  rate_limit: "Too many requests. Please wait a moment and try again.",
  server: "Something went wrong on our end. Please try again later.",
  unknown: "An unexpected error occurred. Please try again.",
};

/**
 * Classify an error based on its type and status code
 */
export function classifyError(error: unknown): ApiErrorType {
  // Network errors (no response received)
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return "network";
  }

  if (error instanceof Error && error.message === "Network request failed") {
    return "network";
  }

  // Check for response status codes
  const statusCode = getStatusCode(error);

  if (statusCode) {
    if (statusCode === 401) return "auth";
    if (statusCode === 403) return "forbidden";
    if (statusCode === 404) return "not_found";
    if (statusCode === 422 || statusCode === 400) return "validation";
    if (statusCode === 429) return "rate_limit";
    if (statusCode >= 500) return "server";
  }

  // Check for specific error patterns
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes("network") || msg.includes("offline")) return "network";
    if (msg.includes("unauthorized") || msg.includes("jwt")) return "auth";
    if (msg.includes("forbidden")) return "forbidden";
    if (msg.includes("not found")) return "not_found";
    if (msg.includes("validation") || msg.includes("invalid")) {
      return "validation";
    }
  }

  return "unknown";
}

/**
 * Extract status code from various error types
 */
function getStatusCode(error: unknown): number | undefined {
  if (typeof error === "object" && error !== null) {
    // Direct status property
    if ("status" in error && typeof error.status === "number") {
      return error.status;
    }
    // Nested in response
    if (
      "response" in error &&
      typeof error.response === "object" &&
      error.response !== null
    ) {
      const response = error.response as Record<string, unknown>;
      if ("status" in response && typeof response.status === "number") {
        return response.status;
      }
    }
    // Supabase error format
    if (hasErrorCode(error)) {
      const code = error.code;
      if (code === SUPABASE_ERROR_CODES.UNAUTHORIZED) return 401;
      if (code === SUPABASE_ERROR_CODES.FORBIDDEN) return 403;
      if (code === SUPABASE_ERROR_CODES.NOT_FOUND) return 404;
      if (code.startsWith("22") || code.startsWith("23")) return 400;
    }
  }
  return undefined;
}

/**
 * Extract error message from various error types
 */
function extractMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "object" && error !== null) {
    if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
    if ("error" in error && typeof error.error === "string") {
      return error.error;
    }
    // Supabase error format
    if (
      "error" in error &&
      typeof error.error === "object" &&
      error.error !== null
    ) {
      const supaError = error.error as Record<string, unknown>;
      if ("message" in supaError && typeof supaError.message === "string") {
        return supaError.message;
      }
    }
  }
  return String(error);
}

/**
 * Create a standardized API error from any error type
 */
export function createApiError(error: unknown): ApiError {
  const type = classifyError(error);
  const message = extractMessage(error);
  const statusCode = getStatusCode(error);

  return {
    type,
    message,
    userMessage: ERROR_MESSAGES[type],
    statusCode,
    originalError: error,
    retryable: type === "network" || type === "server" || type === "rate_limit",
    details:
      typeof error === "object" && error !== null
        ? (error as Record<string, unknown>)
        : undefined,
    traceId: traceContext.getTraceId(),
  };
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  const type = classifyError(error);
  return type === "network" || type === "server" || type === "rate_limit";
}

/**
 * Check if user is offline
 */
export function isOffline(): boolean {
  return typeof navigator !== "undefined" && !navigator.onLine;
}

/**
 * Wait for a specified time (for retry delays)
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry configuration
 */
export interface RetryConfig {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
}

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
};

/**
 * Execute an async function with retry logic
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config: RetryConfig = {},
): Promise<T> {
  const { maxRetries, initialDelayMs, maxDelayMs, backoffMultiplier } = {
    ...DEFAULT_RETRY_CONFIG,
    ...config,
  };

  let lastError: unknown;
  let currentDelay = initialDelayMs;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if error is not retryable
      if (!isRetryableError(error)) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Wait before retrying with exponential backoff
      await delay(currentDelay);
      currentDelay = Math.min(currentDelay * backoffMultiplier, maxDelayMs);
    }
  }

  throw lastError;
}

/**
 * Report an error to monitoring service (Sentry) and structured logger
 */
export function reportApiError(
  error: ApiError,
  context?: Record<string, unknown>,
): void {
  const log = logger.child("ApiError");

  // Log all API errors with structured logger
  log.error(`API Error: ${error.type}`, {
    type: error.type,
    message: error.message,
    statusCode: error.statusCode,
    retryable: error.retryable,
    traceId: error.traceId,
    ...context,
  });

  // Only report server and unknown errors to Sentry to avoid noise
  if (error.type === "server" || error.type === "unknown") {
    Sentry.captureException(error.originalError || new Error(error.message), {
      level: error.type === "server" ? "error" : "warning",
      tags: {
        errorType: error.type,
        statusCode: error.statusCode?.toString() || "unknown",
        traceId: error.traceId,
      },
      contexts: {
        apiError: {
          type: error.type,
          message: error.message,
          userMessage: error.userMessage,
          retryable: error.retryable,
          traceId: error.traceId,
        },
        ...context,
      },
    });
  }
}

/**
 * Handle API errors with standard logging and user notification
 * Returns the user-friendly message
 */
export function handleApiError(
  error: unknown,
  context?: Record<string, unknown>,
): string {
  const apiError = createApiError(error);
  reportApiError(apiError, context);
  return apiError.userMessage;
}

/**
 * Type guard for checking if an error is a specific type
 */
export function isApiErrorType(
  error: unknown,
  type: ApiErrorType,
): error is ApiError {
  return classifyError(error) === type;
}

/**
 * Hook-friendly error handler that returns structured error state
 */
export interface UseApiErrorState {
  error: ApiError | null;
  isError: boolean;
  isNetworkError: boolean;
  isAuthError: boolean;
  isValidationError: boolean;
  isServerError: boolean;
  userMessage: string | null;
  clear: () => void;
}

/**
 * Get trace headers to include in API requests
 * Use this to correlate client requests with server-side logs
 */
export function getTraceHeaders(): Record<string, string> {
  return traceContext.getHeaders();
}

/**
 * Enhanced fetch that automatically includes trace headers
 * Use this for API calls to enable end-to-end tracing
 */
export async function tracedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const traceHeaders = traceContext.getHeaders();
  const headers = new Headers(init?.headers);

  // Add trace headers
  Object.entries(traceHeaders).forEach(([key, value]) => {
    headers.set(key, value);
  });

  return fetch(input, {
    ...init,
    headers,
  });
}
